/**
 * Custom in-cart "Essentials add-ons" (Padel USA)
 * ---------------------------------------------------------------------------
 * SELECT NOW, COMMIT AT CHECKOUT (mirrors the kit-upsell popup pattern).
 *
 * Tapping anywhere on an add-on card does NOT write to the cart — it just SELECTS
 * the product client-side (instant, no fetch, no loading, no cart morph). The
 * selected variant ids are committed in a single /cart/add.js call only when
 * the shopper clicks the cart "Check out" button; we then navigate to checkout.
 * This removes the per-tap latency of the old add-immediately model.
 *
 * Selection state lives in a module-level Set so it SURVIVES cart morphs (the
 * <cart-essentials-addons> element + its cards are re-rendered from server
 * truth whenever something else changes the cart — qty, line removal, shipping
 * protection). After every morph we re-apply the selected visuals and prune any
 * selected id whose card has disappeared (e.g. it ended up in the cart for real,
 * so the batch-1 server-side "hide in-cart add-ons" filter removed it).
 *
 * Checkout commit: a single capturing `submit` listener on document (morph-proof
 * — survives section re-renders that would drop a node-level listener) catches
 * the #cart-form submit fired by the #checkout button (name="checkout"), adds
 * the selected add-ons, then sends the shopper to /checkout. If nothing is
 * selected it no-ops and the native submit proceeds untouched.
 *
 * Known limitation: express/accelerated checkout buttons (Shop Pay, PayPal…)
 * submit their own flow and bypass #checkout, so selected add-ons are not
 * committed through them.
 *
 * Analytics (2026-10-09, upsell ranking step 1 — same contract as assets/build-your-kit.js):
 * `cart_essentials_shown` once per page view, when the carousel first scrolls into view (every
 * rendered card as {slot, product_id, variant_id, price_cents, position, handle, in_view}), and
 * `cart_essentials_added` when the checkout commit succeeds (the committed cards). Both go to
 * dataLayer, a document CustomEvent and Shopify.analytics.publish. The lead cards follow the shop
 * metafield custom.cart_addon_priority (snippets/cart-essentials-addons.liquid).
 *
 * Markup: snippets/cart-essentials-addons.liquid.
 */

import { ensureShippingProtectionTier } from '@theme/shipping-protection-tier';

const CART_ADD_URL = '/cart/add.js';
/** Line-item `_added_from` value and the analytics `placement` label. */
const SOURCE = 'cart-essentials-addons';
/** `cart_essentials_shown` fires once per page view — cart morphs re-create the element. */
let shownTracked = false;

/** Fire-and-forget analytics (same contract as assets/build-your-kit.js #track) — never throws. */
function track(name, detail) {
  try {
    const payload = { ...detail, placement: SOURCE };
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, ...payload });
    document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: payload }));
    window.Shopify?.analytics?.publish?.(name, payload);
  } catch (error) {
    /* analytics must never break the cart */
  }
}

/** Analytics fields for one card; `position` is 1-based in carousel order. */
function cardInfo(item, position) {
  const button = item.querySelector('[data-addon-toggle]');
  return {
    slot: item.dataset.addonSlot || 'other',
    product_id: item.dataset.productId || '',
    variant_id: button?.dataset.variantId || '',
    price_cents: Number(button?.dataset.priceCents) || 0,
    position,
    handle: item.dataset.addonHandle || '',
  };
}

/** Selected add-on variant ids (string). Module-level so it persists across cart morphs. */
const selected = new Set();
const sizeChoices = new Map();
const colorChoices = new Map();

// The cart carousel is narrow, so the same theme variant buttons used in the
// popup live behind a compact chooser. The native select remains the data source.
function buildFlokyChoices(root) {
  root.querySelectorAll('[data-addon-floky]').forEach((item) => {
    const select = item.querySelector('[data-addon-size]');
    const picker = item.querySelector('[data-addon-variant-choices]');
    if (!select || !picker || picker.dataset.built === 'true') return;
    const options = [...select.options].filter((option) => option.value);
    if (!options.length || options.some((option) => !option.dataset.color || !option.dataset.size)) return;
    for (const [kind, values] of [
      ['color', [...new Set(options.map((option) => option.dataset.color))]],
      ['size', [...new Set(options.map((option) => option.dataset.size))]],
    ]) {
      const container = picker.querySelector(`[data-addon-${kind}-options]`);
      for (const value of values) {
        const label = document.createElement('label');
        label.className = 'variant-option__button-label cart-addons__choice';
        const input = document.createElement('input');
        input.className = 'visually-hidden';
        input.type = 'radio';
        input.name = `cart-floky-${kind}-${item.dataset.productId}`;
        input.value = value;
        input.dataset.addonChoice = kind;
        const text = document.createElement('span');
        text.textContent = value;
        label.append(input);
        if (kind === 'color' && /^(black|white)$/i.test(value)) {
          const swatch = document.createElement('span');
          swatch.className = `cart-addons__choice-swatch cart-addons__choice-swatch--${value.toLowerCase()}`;
          swatch.setAttribute('aria-hidden', 'true');
          label.append(swatch);
        }
        label.append(text);
        container.append(label);
      }
    }
    picker.dataset.built = 'true';
    select.closest('.cart-addons__size').hidden = true;
    picker.hidden = false;
  });
}

function openFlokyPopover(item) {
  const picker = item.querySelector('[data-addon-variant-choices]');
  const panel = picker?.querySelector('[data-addon-choice-panel]');
  const trigger = picker?.querySelector('[data-addon-choice-trigger]');
  if (!panel || !trigger || panel.dataset.open === 'true') return;
  panel.hidden = false;
  try { panel.showPopover?.(); } catch { /* fixed-position fallback */ }
  panel.dataset.open = 'true';
  trigger.setAttribute('aria-expanded', 'true');
  panel.querySelector('[data-addon-color-options] input:not(:disabled)')?.focus();
}

function closeFlokyPopover(item, restoreFocus = false) {
  const picker = item?.querySelector('[data-addon-variant-choices]');
  const panel = picker?.querySelector('[data-addon-choice-panel]');
  const trigger = picker?.querySelector('[data-addon-choice-trigger]');
  if (!panel || panel.dataset.open !== 'true') return;
  if (panel.hidePopover && panel.matches(':popover-open')) panel.hidePopover();
  panel.hidden = true;
  panel.dataset.open = 'false';
  trigger?.setAttribute('aria-expanded', 'false');
  if (restoreFocus) trigger?.focus();
}

function syncFlokyChoices(item, select) {
  const picker = item.querySelector('[data-addon-variant-choices]');
  if (!picker || picker.hidden) return;
  const available = [...select.options].filter((option) => option.value && !option.disabled);
  const key = item.dataset.productId;
  const active = select.selectedOptions[0];
  let color = active?.value ? active.dataset.color : colorChoices.get(key) || '';
  if (!available.some((option) => option.dataset.color === color)) color = '';
  if (color) colorChoices.set(key, color);
  else colorChoices.delete(key);
  const size = active?.value ? active.dataset.size : '';
  picker.querySelectorAll('[data-addon-choice]').forEach((input) => {
    const isColor = input.dataset.addonChoice === 'color';
    input.checked = input.value === (isColor ? color : size);
    input.disabled = !available.some((option) =>
      isColor ? option.dataset.color === input.value
        : option.dataset.color === color && option.dataset.size === input.value
    );
  });
  const trigger = picker.querySelector('[data-addon-choice-trigger]');
  const summary = picker.querySelector('[data-addon-choice-summary]');
  const wantedSummary = active?.value ? `${color} / ${size}` : '';
  if (summary && summary.textContent !== wantedSummary) summary.textContent = wantedSummary;
  if (trigger) {
    const hideTrigger = !active?.value;
    if (trigger.hidden !== hideTrigger) trigger.hidden = hideTrigger;
    const triggerLabel = active?.value ? `Change Floky color and size, current ${color} / ${size}` : 'Choose Floky color and size';
    if (trigger.getAttribute('aria-label') !== triggerLabel) trigger.setAttribute('aria-label', triggerLabel);
  }
  const button = item.querySelector('[data-addon-toggle]');
  const buttonText = button?.querySelector('.cart-addons__toggle-add');
  const wantedButtonText = active?.value ? '+ Add' : '+ Choose';
  if (buttonText && buttonText.textContent !== wantedButtonText) buttonText.textContent = wantedButtonText;
  const buttonLabel = active?.value ? 'Add Floky NO STRAIN Evolution Sleeve to cart' : 'Choose color and size for Floky NO STRAIN Evolution Sleeve';
  if (button && button.getAttribute('aria-label') !== buttonLabel) button.setAttribute('aria-label', buttonLabel);
  const hint = picker.querySelector('[data-addon-size-hint]');
  const wantedHint = color ? 'Choose your size.' : 'Choose a color first.';
  if (hint && hint.textContent !== wantedHint) hint.textContent = wantedHint;
  if (!active?.value) {
    const preview = available.find((option) => option.dataset.color === color) || available[0];
    const price = item.querySelector('.cart-addons__price');
    const previewPrice = String(preview?.dataset.price || '').replace(/<[^>]*>/g, '').trim();
    const wantedPrice = previewPrice ? `From ${previewPrice}` : '';
    if (price && price.textContent.trim() !== wantedPrice) price.textContent = wantedPrice;
    const image = item.querySelector('.cart-addons__media img');
    if (image && preview?.dataset.image && image.getAttribute('src') !== preview.dataset.image) {
      image.src = preview.dataset.image;
      image.alt = preview.dataset.alt || '';
    }
  }
}

function restoreSizes(root) {
  root.querySelectorAll('[data-addon-size]').forEach((select) => {
    const item = select.closest('[data-addon-item]');
    const key = item.dataset.productId;
    const id = sizeChoices.get(key) || '';
    const option = [...select.options].find((option) => option.value === id && !option.disabled);
    if (!option || !id) sizeChoices.delete(key);
    select.value = option ? id : '';
    const button = item.querySelector('[data-addon-toggle]');
    button.dataset.variantId = select.value;
    button.disabled = !select.value && !item.hasAttribute('data-addon-floky');
    if (!select.value) {
      syncFlokyChoices(item, select);
      return;
    }
    button.dataset.priceCents = option.dataset.priceCents;
    const price = item.querySelector('.cart-addons__price');
    const text = (option.dataset.compare ? option.dataset.compare + ' ' : '') + option.dataset.price;
    if (price.textContent !== text) {
      price.replaceChildren();
      if (option.dataset.compare) {
        const compare = document.createElement('s');
        compare.className = 'cart-addons__compare';
        compare.textContent = option.dataset.compare;
        price.append(compare, ' ');
      }
      price.append(option.dataset.price);
    }
    const image = item.querySelector('img');
    if (image && option.dataset.image) {
      if (image.getAttribute('src') !== option.dataset.image) image.setAttribute('src', option.dataset.image);
      image.alt = option.dataset.alt || '';
    }
    syncFlokyChoices(item, select);
  });
}

let committing = false;

/**
 * Re-apply the selected visual state to the add-on cards currently in `root`,
 * and drop any selected id whose card is no longer present (it's now in the
 * cart, so it was filtered out server-side — don't double-add it at checkout).
 */
function applySelection(root) {
  restoreSizes(root);
  const present = new Set();
  root.querySelectorAll('[data-addon-toggle]').forEach((button) => {
    const id = button.dataset.variantId;
    if (id) present.add(id);
    const on = selected.has(id);
    // Only write when the value actually differs — keeps applySelection a no-op
    // when the DOM already matches, so the MutationObserver below can't self-loop.
    const want = on ? 'true' : 'false';
    if (button.getAttribute('aria-pressed') !== want) button.setAttribute('aria-pressed', want);
    const item = button.closest('[data-addon-item]');
    if (item && item.classList.contains('is-added') !== on) item.classList.toggle('is-added', on);
  });
  for (const id of [...selected]) {
    if (!present.has(id)) selected.delete(id);
  }
}

// ---- Optimistic grand total (Sub Total + the mobile checkout button price) -----------------
// Add-ons are select-only (not in the cart until checkout), so we optimistically add their prices
// to the displayed total — like shipping-protection's #bumpTotal. We compute the total from FIRST
// PRINCIPLES — merchandise subtotal (server `data-merch`) + the active shipping-protection price +
// the selected add-ons — rather than scraping the displayed total. Reading INPUTS (never the value
// we just wrote) makes writeGrandTotal idempotent: re-running it can't compound or loop, so a single
// broad observer is safe and we need no string guards / disconnect dances.
let addonsDeltaCents = 0;
let hasTakenOverTotal = false; // once true we manage the total; before, leave the pristine render
let subtotalFmt = null; // {prefix, suffix} captured from the server Sub Total format (e.g. "$", " USD")
let checkoutFmt = null; // {prefix, suffix} captured from the server checkout-button total (e.g. "$", "")

const grandTotalEl = () => document.querySelector('.cart-totals__total-value');
const checkoutTotalEls = () => document.querySelectorAll('.cart__checkout-total');
const shipProtectEl = () => document.querySelector('shipping-protection');

// Parse a shop money string into integer cents. Assumes "$1,234.56[ USD]" (US grouping, dot
// decimal) — true for this USD store; revisit if the shop money format ever changes.
function centsFromMoney(text) {
  const cleaned = String(text || '').replace(/[^\d.]/g, '');
  if (!cleaned) return null;
  return Math.round(parseFloat(cleaned) * 100);
}

// {prefix, suffix} around the numeric part, so we can reproduce the server's exact format
// (currency symbol + any trailing code like " USD"); thousands grouping comes from toLocaleString.
function parseFmt(text) {
  const m = String(text || '').match(/^(\D*)[\d.,]+(.*)$/);
  return m ? { prefix: m[1], suffix: m[2] } : { prefix: '$', suffix: '' };
}

function formatMoney(cents, fmt) {
  const amount = (Math.max(0, cents) / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const prefix = fmt && fmt.prefix != null ? fmt.prefix : '$';
  const suffix = fmt && fmt.suffix != null ? fmt.suffix : '';
  return prefix + amount + suffix;
}

function recomputeAddonsDelta() {
  let sum = 0;
  document.querySelectorAll('[data-addon-toggle]').forEach((button) => {
    if (selected.has(button.dataset.variantId)) sum += Number(button.dataset.priceCents) || 0;
  });
  addonsDeltaCents = sum;
}

// The total WITHOUT add-ons = cart merchandise + the active shipping protection. Read merchandise
// from <shipping-protection data-merch> (server-rendered, refreshed by every morph, and unaffected
// by a protection toggle since merch excludes protection) and add the protection price back when
// active (the protection DOM is kept current optimistically). This equals cart.total_price without
// ever reading the displayed total we manage. Fallback: the Sub Total `value` attribute (server
// truth) when no <shipping-protection> is present.
function baseWithoutAddons() {
  const sp = shipProtectEl();
  if (sp && sp.dataset.merch != null && sp.dataset.merch !== '') {
    const merch = parseInt(sp.dataset.merch, 10);
    if (!Number.isNaN(merch)) {
      const active = sp.classList.contains('is-active');
      const protectionCents = active ? centsFromMoney(sp.querySelector('[data-price]')?.textContent) || 0 : 0;
      return merch + protectionCents;
    }
  }
  const el = grandTotalEl();
  return el ? centsFromMoney(el.getAttribute('value') || el.textContent) : null;
}

// Idempotent: total = base (merch + active protection) + selected add-ons. Writes only when the
// formatted value differs, so re-entry from our own DOM writes is a harmless no-op.
function writeGrandTotal() {
  recomputeAddonsDelta();
  // Express buttons (Shop Pay / Apple Pay / PayPal) skip #checkout, so they would drop selected
  // add-ons. Hide them while any add-on is selected; Check out commits the add-ons as usual.
  document.documentElement.classList.toggle('pusa-addons-pending', selected.size > 0);
  // Until the first add-on is selected, leave the pristine server total untouched (keep its format).
  if (addonsDeltaCents === 0 && !hasTakenOverTotal) return;
  const base = baseWithoutAddons();
  if (base == null) return;
  hasTakenOverTotal = true;
  const totalCents = base + addonsDeltaCents;
  const sub = grandTotalEl();
  if (sub) {
    // Capture the server's money format once (value attr is server truth; trim its leading space).
    if (!subtotalFmt) subtotalFmt = parseFmt((sub.getAttribute('value') || sub.textContent || '').trim());
    const text = formatMoney(totalCents, subtotalFmt);
    if (sub.textContent.trim() !== text) sub.textContent = text;
  }
  const cos = checkoutTotalEls();
  if (cos.length && !checkoutFmt) checkoutFmt = parseFmt((cos[0].textContent || '').trim());
  cos.forEach((node) => {
    const text = formatMoney(totalCents, checkoutFmt);
    if (node.textContent.trim() !== text) node.textContent = text;
  });
}

// ---------------------------------------------------------------------------
// Optimistic cart totals on quantity change / line removal (Padel USA perf).
// The grand total used to sit shimmering for the whole /cart/change.js round-trip
// (~1.5-2.5s with the app cart functions). Cart rows carry data-line-unit-price
// (cents; the shipping-protection line is excluded — data-merch excludes it and
// baseWithoutAddons() adds the active protection back). On every quantity event /
// remove click we recompute the merchandise subtotal client-side, write it to
// <shipping-protection data-merch>, force the takeover, and run writeGrandTotal()
// — the exact pipeline the add-ons/protection already use. The server morph
// refreshes data-merch afterwards, so discount-driven drift self-corrects.
function optimisticMerchFromRows(changedRow, changedQuantity) {
  const sp = shipProtectEl();
  if (!sp || sp.dataset.merch == null || sp.dataset.merch === '') return;
  let sum = 0;
  document.querySelectorAll('.cart-items__table-row[data-line-unit-price]').forEach((row) => {
    if (row.classList.contains('removing')) return;
    const unit = parseInt(row.getAttribute('data-line-unit-price') || '', 10);
    if (Number.isNaN(unit)) return;
    let qty;
    if (row === changedRow && changedQuantity != null) {
      qty = changedQuantity;
    } else {
      const input = row.querySelector('quantity-selector-component input, input[data-cart-quantity]');
      qty = input ? parseInt(input.value, 10) : NaN;
    }
    if (Number.isNaN(qty)) qty = 1;
    sum += unit * qty;
  });
  sp.dataset.merch = String(sum);
  hasTakenOverTotal = true;
  writeGrandTotal();
}

(function initOptimisticCartTotals() {
  document.addEventListener('quantity-selector:update', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const row = target.closest('.cart-items__table-row');
    if (!row) return;
    const quantity = /** @type {any} */ (event)?.detail?.quantity;
    optimisticMerchFromRows(row, typeof quantity === 'number' ? quantity : null);
  });
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element) || !target.closest('.cart-items__remove')) return;
      // The row gets its `.removing` class right after this capture handler, in the same
      // click task — recompute just after so the removed row is excluded from the sum.
      setTimeout(() => optimisticMerchFromRows(null, null), 50);
    },
    true
  );
  // Cart errors (e.g. availability caps) never morph — the component resets the input to the
  // server value and fires this so the optimistic total snaps back to reality.
  document.addEventListener('cart:optimistic-resync', () => optimisticMerchFromRows(null, null));
})();

class CartEssentialsAddons extends HTMLElement {
  // The WHOLE card is the toggle (not just the "+ Add" button) so a tap anywhere
  // on it selects/deselects the add-on. There is no product link, so the shopper
  // never leaves the cart page (better checkout conversion). The inner button still
  // holds data-variant-id and stays keyboard-focusable.
  #onChange = (event) => {
    if (committing) return;
    const choice = event.target.closest('[data-addon-choice]');
    if (choice) {
      const item = choice.closest('[data-addon-item]');
      const select = item.querySelector('[data-addon-size]');
      const key = item.dataset.productId;
      selected.delete(item.querySelector('[data-addon-toggle]').dataset.variantId);
      if (choice.dataset.addonChoice === 'color') {
        colorChoices.set(key, choice.value);
        sizeChoices.delete(key);
      } else {
        const option = [...select.options].find((candidate) =>
          candidate.value && !candidate.disabled &&
          candidate.dataset.color === colorChoices.get(key) &&
          candidate.dataset.size === choice.value
        );
        if (option) sizeChoices.set(key, option.value);
      }
      applySelection(this);
      writeGrandTotal();
      if (choice.dataset.addonChoice === 'size' && sizeChoices.has(key)) {
        closeFlokyPopover(item);
        item.querySelector('[data-addon-toggle]')?.focus();
      }
      return;
    }
    const select = event.target.closest('[data-addon-size]');
    if (!select) return;
    const item = select.closest('[data-addon-item]');
    selected.delete(item.querySelector('[data-addon-toggle]').dataset.variantId);
    sizeChoices.set(item.dataset.productId, select.value);
    if (select.value) colorChoices.set(item.dataset.productId, select.selectedOptions[0].dataset.color || '');
    applySelection(this);
    writeGrandTotal();
  };

  #onClick = (event) => {
    if (committing) return;
    const close = event.target.closest('[data-addon-choice-close]');
    if (close && this.contains(close)) {
      event.preventDefault();
      closeFlokyPopover(close.closest('[data-addon-item]'), true);
      return;
    }
    const chooser = event.target.closest('[data-addon-choice-trigger]');
    if (chooser && this.contains(chooser)) {
      event.preventDefault();
      const item = chooser.closest('[data-addon-item]');
      if (item.querySelector('[data-addon-choice-panel]').dataset.open === 'true') closeFlokyPopover(item, true);
      else openFlokyPopover(item);
      return;
    }
    if (event.target.closest('select, option, label, [data-addon-variant-choices]')) return;
    // Desktop carousel arrows.
    const arrow = event.target.closest('[data-addon-prev], [data-addon-next]');
    if (arrow && this.contains(arrow)) {
      event.preventDefault();
      this.#scrollList(arrow.hasAttribute('data-addon-next') ? 1 : -1);
      return;
    }
    const item = event.target.closest('[data-addon-item]');
    if (!item || !this.contains(item)) return;
    const button = item.querySelector('[data-addon-toggle]');
    if (!button) return;
    event.preventDefault();
    if (item.hasAttribute('data-addon-floky') && !button.dataset.variantId) {
      openFlokyPopover(item);
      return;
    }
    this.#toggle(button);
  };

  #onChoiceOutside = (event) => {
    const item = this.querySelector('[data-addon-floky]');
    const panel = item?.querySelector('[data-addon-choice-panel]');
    const target = event.target;
    if (panel?.dataset.open !== 'true' || !(target instanceof Element)) return;
    if (panel.contains(target) || target.closest('[data-addon-choice-trigger]')) return;
    closeFlokyPopover(item);
  };

  #onChoiceEscape = (event) => {
    const item = this.querySelector('[data-addon-floky]');
    if (event.key !== 'Escape' || item?.querySelector('[data-addon-choice-panel]')?.dataset.open !== 'true') return;
    event.preventDefault();
    event.stopPropagation();
    closeFlokyPopover(item, true);
  };

  #observer = null;
  #boundList = null;
  #scrollAnim = null;
  #scrollSafety = null;
  #animating = false;
  // Mouse drag-to-scroll (desktop pointer). { startX, startScroll, moved, pointerId } while a
  // drag is in progress, else null. #suppressClick swallows the click synthesized after a drag
  // so dragging the carousel never toggles an add-on card.
  #dragState = null;
  #suppressClick = false;

  // Toggle the prev/next disabled state (and hide the arrows entirely when nothing overflows).
  #updateArrows = () => {
    if (this.#animating) return; // the in-flight scroll tween updates the arrow state itself
    const list = this.querySelector('.cart-addons__list');
    if (!list) return;
    const max = list.scrollWidth - list.clientWidth;
    const arrows = this.querySelector('.cart-addons__arrows');
    if (arrows) arrows.classList.toggle('is-hidden', max <= 1);
    const prev = this.querySelector('[data-addon-prev]');
    const next = this.querySelector('[data-addon-next]');
    if (prev) prev.disabled = list.scrollLeft <= 1;
    if (next) next.disabled = list.scrollLeft >= max - 1;
  };

  // Page the carousel by a full "page" in `dir` (+1 next, -1 prev): advance by the number of FULLY
  // visible cards so the partially-hidden next card becomes the new first card, with a smooth eased
  // transition. Driven by requestAnimationFrame (vsync-smooth); per-frame work is minimal — a cheap
  // inline arrow-disable using cached refs, and #updateArrows skips its heavier layout reads while
  // #animating (so the scroll listener doesn't double-thrash). scroll-snap is paused during the tween
  // and restored after so it settles on a card edge. A safety timer lands the scroll if rAF never
  // fires (e.g. a hidden / headless page).
  #scrollList(dir) {
    const list = this.querySelector('.cart-addons__list');
    if (!list) return;
    const cards = list.querySelectorAll('.cart-addons__item');
    if (!cards.length) return;
    // Stride = distance between adjacent card starts (card width + gap), read from the DOM.
    const stride =
      cards.length >= 2
        ? cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left
        : cards[0].getBoundingClientRect().width + 12;
    const perView = Math.max(1, Math.floor(list.clientWidth / stride));
    const max = list.scrollWidth - list.clientWidth;
    const target = Math.max(0, Math.min(max, list.scrollLeft + dir * perView * stride));

    this.#stopScrollAnim();
    if (target === list.scrollLeft) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      list.scrollLeft = target;
      this.#updateArrows();
      return;
    }

    const start = list.scrollLeft;
    const distance = target - start;
    const duration = 450;
    const t0 = performance.now();
    const prev = this.querySelector('[data-addon-prev]');
    const next = this.querySelector('[data-addon-next]');
    list.style.scrollSnapType = 'none';
    this.#animating = true;

    const finish = () => {
      list.scrollLeft = target;
      this.#stopScrollAnim(); // clears rAF + safety, unsets #animating, restores snap
      this.#updateArrows();
    };
    const tick = (now) => {
      if (!this.#animating) return;
      const p = Math.min(1, (now - t0) / duration);
      list.scrollLeft = start + distance * (1 - Math.pow(1 - p, 3)); // easeOutCubic
      if (prev) prev.disabled = list.scrollLeft <= 1;
      if (next) next.disabled = list.scrollLeft >= max - 1;
      if (p < 1) this.#scrollAnim = requestAnimationFrame(tick);
      else finish();
    };
    this.#scrollAnim = requestAnimationFrame(tick);
    this.#scrollSafety = setTimeout(finish, duration + 120);
  }

  #stopScrollAnim() {
    if (this.#scrollAnim != null) {
      cancelAnimationFrame(this.#scrollAnim);
      this.#scrollAnim = null;
    }
    if (this.#scrollSafety != null) {
      clearTimeout(this.#scrollSafety);
      this.#scrollSafety = null;
    }
    this.#animating = false;
    const list = this.querySelector('.cart-addons__list');
    if (list) list.style.scrollSnapType = ''; // restore snap so it lands on a card edge
  }

  // (Re)bind the scroll listener if a morph replaced the list element, then refresh the arrow state.
  #bindArrows() {
    const list = this.querySelector('.cart-addons__list');
    if (list && list !== this.#boundList) {
      this.#boundList?.removeEventListener('scroll', this.#updateArrows);
      this.#boundList?.removeEventListener('pointerdown', this.#onDragStart);
      this.#boundList = list;
      list.addEventListener('scroll', this.#updateArrows, { passive: true });
      list.addEventListener('pointerdown', this.#onDragStart);
    }
    this.#updateArrows();
  }

  // ---- Mouse drag-to-scroll ---------------------------------------------------------------
  // Click-and-drag the carousel with a mouse, like the store's other sliders. Mouse-only:
  // touch devices keep their native momentum scrolling (we never preventDefault there). A drag
  // past a small threshold sets #suppressClick so the trailing click can't toggle a card.
  #onDragStart = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('[data-addon-prev], [data-addon-next], select, label')) return; // let arrows click
    const list = this.#boundList;
    if (!list) return;
    this.#stopScrollAnim(); // cancel any in-flight arrow tween before a manual drag
    this.#suppressClick = false;
    this.#dragState = { startX: event.clientX, startScroll: list.scrollLeft, moved: false, pointerId: event.pointerId };
    list.addEventListener('pointermove', this.#onDragMove);
    list.addEventListener('pointerup', this.#onDragEnd);
    list.addEventListener('pointercancel', this.#onDragEnd);
  };

  #onDragMove = (event) => {
    const drag = this.#dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const dx = event.clientX - drag.startX;
    if (!drag.moved) {
      if (Math.abs(dx) < 4) return; // tiny movement = a click, not a drag
      drag.moved = true;
      const list = this.#boundList;
      list.classList.add('is-dragging'); // grabbing cursor + user-select:none (CSS)
      list.style.scrollSnapType = 'none'; // free drag; restored on release so it settles on a card
      try { list.setPointerCapture(drag.pointerId); } catch (_) {}
    }
    event.preventDefault(); // stop text/image selection while dragging
    this.#boundList.scrollLeft = drag.startScroll - dx;
  };

  #onDragEnd = (event) => {
    const drag = this.#dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const list = this.#boundList;
    list.removeEventListener('pointermove', this.#onDragMove);
    list.removeEventListener('pointerup', this.#onDragEnd);
    list.removeEventListener('pointercancel', this.#onDragEnd);
    try { list.releasePointerCapture(drag.pointerId); } catch (_) {}
    if (drag.moved) {
      list.classList.remove('is-dragging');
      list.style.scrollSnapType = ''; // restore snap → settles on a card edge
      this.#suppressClick = true; // swallow the click that follows the drag (don't toggle a card)
      this.#updateArrows();
    }
    this.#dragState = null;
  };

  // Capturing click handler (runs before #onClick) — eats exactly one click after a drag.
  #onClickCapture = (event) => {
    if (!this.#suppressClick) return;
    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  connectedCallback() {
    this.addEventListener('click', this.#onClickCapture, true); // capture: pre-empts #onClick after a drag
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    document.addEventListener('pointerdown', this.#onChoiceOutside, true);
    document.addEventListener('keydown', this.#onChoiceEscape, true);
    buildFlokyChoices(this);
    applySelection(this);
    writeGrandTotal(); // no-op until the first add-on is selected
    this.#bindArrows();
    window.addEventListener('resize', this.#updateArrows, { passive: true });
    this.#watchShown();

    // One observer over the whole cart section. The total is computed from inputs (merch +
    // protection + selected add-ons), so writeGrandTotal is idempotent — reacting to ANY mutation
    // (a morph re-rendering merch, a shipping-protection toggle, or our own card writes) is safe and
    // converges without loops. applySelection re-applies the selected card visuals after a morph.
    const scope = this.closest('cart-items-component') || document.body;
    this.#observer = new MutationObserver(() => {
      applySelection(this);
      writeGrandTotal();
      this.#bindArrows();
    });
    this.#observer.observe(scope, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['aria-pressed', 'class', 'data-merch'],
    });
  }

  /**
   * Impression = the carousel is at least a quarter on screen (it often sits below the fold on
   * mobile). Each card reports `in_view` — at least half of it inside the scroller at that moment.
   */
  #watchShown() {
    if (shownTracked || typeof IntersectionObserver === 'undefined') return;
    this.#shownObserver = new IntersectionObserver(
      (entries) => {
        if (shownTracked || !entries.some((entry) => entry.isIntersecting)) return;
        shownTracked = true;
        this.#shownObserver?.disconnect();
        const list = this.querySelector('.cart-addons__list')?.getBoundingClientRect();
        const items = [...this.querySelectorAll('[data-addon-item]')].map((item, index) => {
          const rect = item.getBoundingClientRect();
          const visible = list ? Math.min(rect.right, list.right) - Math.max(rect.left, list.left) : rect.width;
          return { ...cardInfo(item, index + 1), in_view: rect.width > 0 && visible >= rect.width / 2 };
        });
        if (items.length) track('cart_essentials_shown', { items });
      },
      { threshold: 0.25 }
    );
    this.#shownObserver.observe(this);
  }

  #shownObserver = null;

  disconnectedCallback() {
    this.#shownObserver?.disconnect();
    this.#shownObserver = null;
    this.removeEventListener('click', this.#onClickCapture, true);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    document.removeEventListener('pointerdown', this.#onChoiceOutside, true);
    document.removeEventListener('keydown', this.#onChoiceEscape, true);
    closeFlokyPopover(this.querySelector('[data-addon-floky]'));
    this.#observer?.disconnect();
    this.#observer = null;
    window.removeEventListener('resize', this.#updateArrows);
    this.#boundList?.removeEventListener('scroll', this.#updateArrows);
    this.#boundList?.removeEventListener('pointerdown', this.#onDragStart);
    this.#boundList?.removeEventListener('pointermove', this.#onDragMove);
    this.#boundList?.removeEventListener('pointerup', this.#onDragEnd);
    this.#boundList?.removeEventListener('pointercancel', this.#onDragEnd);
    this.#boundList = null;
    this.#stopScrollAnim();
  }

  #toggle(button) {
    const id = button.dataset.variantId;
    if (!id || button.disabled || committing) return;
    if (selected.has(id)) {
      selected.delete(id);
    } else {
      selected.add(id);
    }
    const on = selected.has(id);
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    button.closest('[data-addon-item]')?.classList.toggle('is-added', on);
    writeGrandTotal(); // recomputes the add-ons delta internally, then re-writes the total
  }
}

if (!customElements.get('cart-essentials-addons')) {
  customElements.define('cart-essentials-addons', CartEssentialsAddons);
}

/**
 * Commit-on-checkout hook. Registered once (module loads once). A capturing
 * submit listener on document is morph-proof — it keeps working even when the
 * cart section (and its #cart-form / #checkout button) is re-rendered.
 *
 * It ALSO drives the checkout button's loading state: on EVERY "Check out" click
 * the button shows a spinner (like the add-to-cart ring) while the browser
 * navigates to checkout — whether or not there are selected add-ons to commit.
 */
if (!window.__cartAddonsCheckoutHook) {
  window.__cartAddonsCheckoutHook = true;

  const setCheckoutBusy = (busy) => {
    const button = document.getElementById('checkout');
    if (!button) return;
    if (busy) {
      button.setAttribute('aria-busy', 'true');
      button.classList.add('is-committing');
    } else {
      button.removeAttribute('aria-busy');
      button.classList.remove('is-committing');
    }
  };

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
      // Only act on the real "Check out" submitter — not Enter in a qty field.
      if (event.submitter?.name !== 'checkout') return;
      if (committing) {
        event.preventDefault(); // already committing/navigating — ignore re-clicks
        return;
      }
      applySelection(document);
      committing = true;

      // Show the loading spinner on every checkout — the redirect takes a moment.
      setCheckoutBusy(true);
      // Safety: if navigation is somehow blocked, don't leave the button stuck.
      const busyTimeout = setTimeout(() => {
        committing = false;
        setCheckoutBusy(false);
      }, 12000);

      if (!selected.size) return; // nothing to commit — native submit proceeds to checkout

      // Commit the selected add-ons first, then navigate to checkout.
      event.preventDefault();
      clearTimeout(busyTimeout); // never unlock a still-pending batch
      document.getElementById('cart-addons-error')?.remove();
      // Hidden (_-prefixed) line item property tags the source so popup vs in-cart add-on
      // revenue can be told apart in the order admin / exports / Flow / Klaviyo. Underscore
      // = not shown in cart/checkout UI, but recorded on the order line. Mirrors kit-upsell.js.
      const items = [...selected].map((id) => ({
        id: Number(id),
        quantity: 1,
        properties: { _added_from: SOURCE },
      }));
      const cards = [...document.querySelectorAll('cart-essentials-addons [data-addon-item]')];
      const addedInfo = cards
        .map((item, index) => ({ item, position: index + 1 }))
        .filter(({ item }) => selected.has(item.querySelector('[data-addon-toggle]')?.dataset.variantId))
        .map(({ item, position }) => cardInfo(item, position));
      try {
        const response = await fetch(CART_ADD_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!response.ok) {
          const error = await response.json().catch(() => ({}));
          throw new Error(error.description || 'Unable to add your extras. Please try again or deselect them.');
        }
        track('cart_essentials_added', { items: addedInfo });
        selected.clear();
      } catch (error) {
        committing = false;
        setCheckoutBusy(false);
        const message = document.createElement('p');
        message.id = 'cart-addons-error';
        message.setAttribute('role', 'alert');
        message.tabIndex = -1;
        message.textContent = error.message || 'Unable to add your extras. Please try again or deselect them.';
        (document.getElementById('checkout') || form).after(message);
        message.focus();
        return;
      }
      // The add-ons may have pushed the cart over a Shipping Protection threshold, and this
      // path skips the toggle's cart:update re-tier. Correct it before leaving.
      await ensureShippingProtectionTier();
      window.location.href = '/checkout';
    },
    true
  );

  // If the shopper hits Back from checkout and the cart page is restored from the
  // bfcache, the button's loading state (and the committing flag) would be frozen
  // in place — clear it so checkout is clickable again.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      committing = false;
      setCheckoutBusy(false);
    }
  });
}
